/**
 * PAGE-LOADER
 * Container of page with loading status
 */
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { STATUS_LOAD_ERROR, STATUS_LOADING } from "@/redux/_status.types"
import { useEffect, useRef, useState } from "react"
import PagePlaceholder from "./page-placeholder"
import { AnimatePresence, motion } from "framer-motion"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"

interface OwnProps {
  children?: any
  minHeight?: number
  forceLoading?: boolean
  isFlex?: boolean
  status: string | null //Status
  className?: string //Class for the container
  onRefresh?: Function //Action on refresh when data not loaded
  skeleton?: React.ReactNode //Remplace le spinner par défaut pendant STATUS_LOADING si fourni
}

function PageLoader(props: OwnProps) {
  const [isLoading, setIsLoading] = useState(props.forceLoading ? true : false)

  //Height of the last content displayed
  //Kept while reloading so the page does not scroll back to the top
  const containerRef = useRef<HTMLDivElement>(null)
  const contentHeight = useRef<number>(0)

  const wasShowingLoader = useRef<boolean>(true)

  const isShowingLoader = props.status === STATUS_LOADING || isLoading

  //Fondu entre le skeleton et le contenu : seulement pour les chargements qui ont un skeleton (opt-in), pour ne pas
  //ajouter un délai à tous les autres chargements de l'app. Coupé pendant un export.
  const isAnimationsDisabled = useAnimationsDisabled()
  const isFading = Boolean(props.skeleton) && !isAnimationsDisabled

  //Measure when the loading starts (the DOM still has the content)
  if (isShowingLoader && !wasShowingLoader.current && containerRef.current) {
    contentHeight.current = containerRef.current.offsetHeight
  }
  wasShowingLoader.current = isShowingLoader

  useEffect(() => {
    if (props.forceLoading) {
      setTimeout(() => {
        setIsLoading(false)
      }, 150)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={`flex flex-dcol page-loader ${props.className ? props.className : ""}`}
      style={{
        minHeight: Math.max(props.minHeight ?? 80, isShowingLoader ? contentHeight.current : 0) + "px"
      }}>
      {isFading ? (
        <AnimatePresence mode="wait">
          {isShowingLoader ? (
            <motion.div key="loading" exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
              {props.skeleton}
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.22 }}
              className={props.isFlex ? "flex" : ""}>
              {props.children}
            </motion.div>
          )}
        </AnimatePresence>
      ) : isShowingLoader ? (
        props.skeleton ?? (
          //Sticky to stay visible in the kept height
          <div
            className="load-container"
            style={contentHeight.current > 0 ? { position: "sticky", top: "40vh", marginTop: 0 } : undefined}>
            <FontAwesomeIcon icon={faCircleNotch} spin />
          </div>
        )
      ) : props.status === STATUS_LOAD_ERROR && props.onRefresh ? (
        <PagePlaceholder
          title="Erreur de chargement"
          image="sherlock"
          button="Rafraîchir"
          onClick={() => (props.onRefresh ? props.onRefresh() : undefined)}
          help="Veuillez rafraîchir la page ou contactez le support à l'adresse support@id-tree.com."
        />
      ) : (
        <div className={props.isFlex ? "flex flex-1 min-h-0" : ""}>{props.children}</div>
      )}
    </div>
  )
}

export default PageLoader
